// ─────────────────────────────────────────────────────────────────
// Glass-i — Dispatch → Delivery Window cockpit (Stage DISPATCH-WINDOW-1).
//
// A read-only cockpit: pick a delivery window → see every committed work order
// due in it, how much of each is ready to dispatch vs pending, the glass area
// and (where the exact glass mass is known) weight, a per-day delivery timeline,
// and a per-work-order line-items popup. All figures are live off production;
// nothing here ships, scans, or writes.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Tag, Typography, Button, Space, Segmented, Select,
  DatePicker, Switch, Alert, Spin, Modal, Tooltip, Progress } from 'antd';
import { ReloadOutlined, WarningOutlined, UnorderedListOutlined, CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { deliveryWindowApi } from './deliveryWindowApi';
import {
  PERM, hasPerm, formatArea, formatWeight, formatInt, formatThickness,
  fmtDay, dow, readinessTag, presetRange, PRESET_ORDER, PRESET_LABELS
} from './deliveryWindowHelpers';
import useAuth from '../../../hooks/useAuth';
import './deliveryWindow.css';

const { Text } = Typography;
const { RangePicker } = DatePicker;
const errMsg = (e, fb) => e?.response?.data?.error?.message || e?.message || fb;
const ROW_CAP = 500;                              // fetched detail-row cap per window

// ── Line-items popup: lazy per-WO readiness breakdown ──
function LineItemsModal({ open, onClose, row }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !row) { setData(null); setError(null); return; }
    let alive = true;
    setLoading(true); setError(null);
    deliveryWindowApi.getWorkOrderLines(row.workOrderId)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(errMsg(e, 'Could not load line items.')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, row]);

  const totalSqm = useMemo(() => {
    if (!data || !data.lines) return 0;
    return data.lines.reduce((a, l) => {
      const w = Number(l.widthMm), h = Number(l.heightMm), q = Number(l.qty) || 0;
      return a + (w > 0 && h > 0 ? (w * h) / 1e6 * q : 0);
    }, 0);
  }, [data]);

  const columns = [
    { title: 'Type', dataIndex: 'type', width: 110, render: (v) => (v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>) },
    { title: 'Item description', dataIndex: 'itemDesc',
      render: (v, r) => (
        <span>{v || <Text type="secondary">—</Text>}
          {r.thicknessMm != null ? <Text type="secondary" className="tnum">{`  ${formatThickness(r.thicknessMm)} mm`}</Text> : null}
          {r.jumbo ? <Tag color="purple" style={{ marginLeft: 6 }}>Jumbo</Tag> : null}
        </span>) },
    { title: 'L×B (mm)', key: 'lxb', width: 130, align: 'right', className: 'tnum',
      render: (_, r) => (r.widthMm != null && r.heightMm != null ? `${r.widthMm} × ${r.heightMm}` : '—') },
    { title: 'Qty', dataIndex: 'qty', width: 70, align: 'right', className: 'tnum' },
    { title: 'Ready (RD)', dataIndex: 'ready', width: 100, align: 'right',
      render: (v) => <span className="tnum" style={{ color: '#16a34a', fontWeight: 600 }}>{v}</span> },
    { title: 'Pending', dataIndex: 'pending', width: 90, align: 'right',
      render: (v) => <span className="tnum" style={{ color: v > 0 ? '#b45309' : undefined }}>{v}</span> }
  ];

  const t = data && data.totals ? data.totals : { committed: 0, ready: 0, pending: 0, lineCount: 0 };

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={820} destroyOnClose
      title={<Space><UnorderedListOutlined /><span>Work order · line items</span></Space>}>
      {row ? (
        <div className="dw-lm-sub">
          <span className="dw-lm-cust">{(data && data.customerName) || row.customerName || '—'}</span>
          <span className="dw-lm-stat"><i>Delivery</i><b>{fmtDay((data && data.deliveryDate) || row.deliveryDate)}</b></span>
          <span className="dw-lm-stat"><b>{formatInt(t.committed)}</b><i>pcs</i></span>
          <span className="dw-lm-stat"><b>{formatArea(totalSqm)}</b><i>m²</i></span>
        </div>
      ) : null}
      {error ? <Alert type="error" showIcon className="mt-3" message={error} /> : null}
      <Table
        rowKey="lineNo" size="small" className="mt-3" loading={loading}
        columns={columns} dataSource={(data && data.lines) || []} pagination={false}
        scroll={{ x: 'max-content', y: 380 }}
        locale={{ emptyText: loading ? ' ' : 'No line items on this work order.' }}
        summary={() => (data && data.lines && data.lines.length ? (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={3}><b>{`Total · ${t.lineCount} line${t.lineCount === 1 ? '' : 's'}`}</b></Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right"><b className="tnum">{t.committed}</b></Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="right"><b className="tnum" style={{ color: '#16a34a' }}>{t.ready}</b></Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right"><b className="tnum" style={{ color: t.pending > 0 ? '#b45309' : undefined }}>{t.pending}</b></Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        ) : null)}
      />
    </Modal>
  );
}

// ── Delivery timeline (per-day committed/ready bars) ──
// Continuous calendar strip: every day across the data span is rendered (empty
// days included), bars stack ready(green) over pending(amber), the selected
// window is shaded, today is outlined. `timeline` is the broad per-day series
// from the API (spans beyond the window); the window is just highlighted here.
const MAX_TIMELINE_DAYS = 240;
function Timeline({ timeline, from, to, onPickDay }) {
  const todayIso = dayjs().format('YYYY-MM-DD');
  const byDay = useMemo(() => {
    const m = new Map();
    for (const d of timeline) m.set(d.date, d);
    return m;
  }, [timeline]);
  const days = useMemo(() => {
    const bounds = [from, to, todayIso, ...timeline.map((d) => d.date)].filter(Boolean).sort();
    if (!bounds.length) return [];
    let start = dayjs(bounds[0]).subtract(2, 'day');
    start = start.subtract((start.day() + 6) % 7, 'day');          // align back to Monday
    const end = dayjs(bounds[bounds.length - 1]).add(2, 'day');
    const list = [];
    for (let cur = start; !cur.isAfter(end) && list.length < MAX_TIMELINE_DAYS; cur = cur.add(1, 'day')) {
      list.push(cur.format('YYYY-MM-DD'));
    }
    return list;
  }, [timeline, from, to, todayIso]);
  const maxTot = Math.max(1, ...timeline.map((d) => d.committed));

  return (
    <div className="dw-timeline-scroll">
      <div className="dw-timeline" style={{ minWidth: Math.max(520, days.length * 24) }}>
        {days.map((iso) => {
          const agg = byDay.get(iso);
          const tot = agg ? agg.committed : 0;
          const rdy = agg ? agg.ready : 0;
          const h = tot ? Math.max(6, Math.round((tot / maxTot) * 52)) : 0;
          const rh = tot ? Math.round((rdy / tot) * h) : 0;
          const inwin = iso >= from && iso <= to;
          return (
            <div key={iso} className={`dw-day ${inwin ? 'dw-inwin' : ''} ${iso === todayIso ? 'dw-today' : ''}`}
              onClick={() => onPickDay(iso)} title={agg ? `${fmtDay(iso)} — ${tot} pcs (${rdy} ready)` : fmtDay(iso)}>
              <div className="dw-bar-area">
                <div className={`dw-stack ${tot ? '' : 'dw-empty'}`} style={{ height: h }}>
                  <div className="dw-seg-ready" style={{ height: rh }} />
                  <div className="dw-seg-pend" style={{ height: h - rh }} />
                </div>
              </div>
              <div className="dw-dlbl"><span className="dw-dnum">{Number(iso.slice(8, 10))}</span>{dow(iso)}</div>
            </div>
          );
        })}
      </div>
      <div className="dw-legend">
        <span><i className="dw-swatch" style={{ background: '#16a34a' }} /> Ready to dispatch</span>
        <span><i className="dw-swatch" style={{ background: '#e0a63a' }} /> Still in production</span>
        <span><i className="dw-swatch dw-swatch-win" /> Selected window</span>
        <span style={{ marginLeft: 'auto' }}>Showing <b>{fmtDay(from)} → {fmtDay(to)}</b></span>
      </div>
    </div>
  );
}

export default function DeliveryWindowPage() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const canView = hasPerm(me, PERM.VIEW);

  const [preset, setPreset] = useState('fortnight');
  const [range, setRange] = useState(() => presetRange('fortnight'));
  const [groupBy, setGroupBy] = useState('date');
  const [includeCompleted, setIncludeCompleted] = useState(true);
  const [lateOnly, setLateOnly] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState({ open: false, row: null });

  // Late-only overrides the chosen window with a wide "everything overdue" span
  // (past year → today) so no backlog is hidden by the current preset; the user's
  // own range/preset is preserved untouched and simply restored when it's off.
  const effRange = useMemo(
    () => (lateOnly ? [dayjs().subtract(365, 'day').startOf('day'), dayjs().startOf('day')] : range),
    [lateOnly, range]
  );

  const load = useCallback(async () => {
    if (!effRange || !effRange[0] || !effRange[1]) return;
    setLoading(true); setError(null);
    try {
      const res = await deliveryWindowApi.getWindow({
        deliveryDateFrom: effRange[0].format('YYYY-MM-DD'),
        deliveryDateTo: effRange[1].format('YYYY-MM-DD'),
        includeCompleted: includeCompleted ? undefined : 'false',
        overdueOnly: lateOnly ? 'true' : undefined,
        pageSize: ROW_CAP
      });
      setData(res);
    } catch (e) { setError(errMsg(e, 'Could not load the delivery window.')); setData(null); }
    finally { setLoading(false); }
  }, [effRange, includeCompleted, lateOnly]);
  useEffect(() => { load(); }, [load]);

  const applyPreset = (key) => { setPreset(key); setRange(presetRange(key)); };
  // Tapping a day on the timeline focuses that single day.
  const onPickDay = (iso) => { setPreset(null); setRange([dayjs(iso), dayjs(iso)]); };

  const kpis = data ? data.kpis : null;
  const rows = useMemo(() => (data && data.rows) || [], [data]);
  const total = data ? data.total : 0;
  const capped = total > rows.length;
  const jumboN = useMemo(() => rows.filter((r) => r.jumbo).length, [rows]);
  const fromIso = effRange && effRange[0] ? effRange[0].format('YYYY-MM-DD') : '';
  const toIso = effRange && effRange[1] ? effRange[1].format('YYYY-MM-DD') : '';

  const columns = useMemo(() => ([
    { title: 'WO', dataIndex: 'woNo', width: 128, fixed: 'left',
      render: (v, r) => <Button type="link" size="small" style={{ padding: 0, height: 'auto', fontWeight: 600 }}
        onClick={() => navigate(`/erp/production/work-orders/${r.workOrderId}`)}>{v}</Button> },
    { title: 'Customer', dataIndex: 'customerName', width: 158, ellipsis: true, render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Delivery', dataIndex: 'deliveryDate', width: 104,
      render: (v, r) => <span className="tnum">{fmtDay(v)}{r.overdue ? <Tag color="red" style={{ marginLeft: 4 }}>Late</Tag> : null}</span> },
    { title: 'Glass', key: 'glass', minWidth: 200,
      render: (_, r) => (
        <span>{r.materialHead || <Text type="secondary">—</Text>}
          {r.thicks ? <span className="dw-sub tnum">{`  ${formatThickness(r.thicks)} mm`}</span> : null}
          {r.types ? (r.types.indexOf(',') >= 0
            ? <Tag color="gold" style={{ marginLeft: 6 }}>Mixed</Tag>
            : <Tag color="blue" style={{ marginLeft: 6 }}>{r.types}</Tag>) : null}
        </span>) },
    { title: 'Lines', key: 'items', width: 92, align: 'center',
      render: (_, r) => (
        <Tooltip title="View line items">
          <Button size="small" className="dw-items-btn" icon={<UnorderedListOutlined />} onClick={() => setModal({ open: true, row: r })}>
            {r.lineCount}{r.jumbo ? <Tag color="purple" style={{ marginLeft: 4 }}>J</Tag> : null}
          </Button>
        </Tooltip>) },
    { title: 'Ready', key: 'ready', width: 150,
      render: (_, r) => {
        const pct = r.committed > 0 ? Math.round((r.ready / r.committed) * 100) : 0;
        return (
          <Space size={6} style={{ width: '100%' }}>
            <Progress percent={pct} showInfo={false} size="small" strokeColor="#16a34a" style={{ width: 74, margin: 0 }} />
            <span className="tnum" style={{ fontSize: 12, whiteSpace: 'nowrap' }}><b>{r.ready}</b>/{r.committed}</span>
          </Space>); } },
    { title: 'Status', dataIndex: 'readinessStatus', width: 128,
      render: (v) => { const t = readinessTag(v); return <Tag color={t.color}>{t.label}</Tag>; } },
    { title: 'Area', dataIndex: 'sqm', width: 108, align: 'right',
      render: (v, r) => (
        <div style={{ lineHeight: 1.15 }}>
          <div className="tnum">{formatArea(v)} <span className="dw-sub">m²</span></div>
          <div className="dw-sub tnum">{formatArea(r.sqft)} ft²</div>
        </div>) },
    { title: 'Weight', dataIndex: 'weightKg', width: 92, align: 'right', className: 'tnum',
      render: (v, r) => (r.weightExact && v != null
        ? <span>{formatWeight(v)} <span className="dw-sub">kg</span></span>
        : <Tooltip title="Exact glass mass isn't known for every line on this order (e.g. a laminate/DGU with no pane breakdown), so weight is left blank."><Text type="secondary">—</Text></Tooltip>) }
  ]), [navigate]);

  // Group loaded rows for the grouped-section rendering.
  const groups = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = groupBy === 'customer' ? (r.customerName || '—') : (r.deliveryDate || '—');
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    const keys = [...map.keys()].sort();
    return keys.map((k) => {
      const g = map.get(k);
      const c = g.reduce((a, x) => a + x.committed, 0);
      const rd = g.reduce((a, x) => a + x.ready, 0);
      const s = g.reduce((a, x) => a + x.sqm, 0);
      return { key: k, rows: g, committed: c, ready: rd, sqm: s };
    });
  }, [rows, groupBy]);

  if (!canView) {
    return <div className="p-4"><Alert type="warning" showIcon message="You don't have access to the Delivery Window." /></div>;
  }

  return (
    <div className="p-4">
      {/* Controls — single-row bar: breadcrumb · presets · range | group-by · completed · refresh */}
      <Card size="small" className="mb-2" bodyStyle={{ padding: '8px 12px' }}>
        <div className="dw-bar">
          <div className="dw-crumb">
            <span className="dw-crumb-dim">Dispatch</span>
            <span className="dw-crumb-sep">/</span>
            <span className="dw-crumb-strong">Delivery Window</span>
          </div>
          <span className="dw-div" />
          <Select size="small" className="dw-preset" popupClassName="dw-preset-pop" disabled={lateOnly}
            value={preset || undefined} placeholder="Custom" style={{ width: 148 }} onChange={applyPreset}
            options={PRESET_ORDER.map((k) => ({ label: PRESET_LABELS[k], value: k }))} />
          <RangePicker size="small" value={effRange} format="DD MMM" allowClear allowEmpty={[false, false]} disabled={lateOnly}
            prefix={<CalendarOutlined style={{ color: '#8a94a6' }} />} suffixIcon={null}
            onChange={(v) => { if (!v || !v[0] || !v[1]) { applyPreset('all'); } else { setPreset(null); setRange(v); } }} />
          <span className="dw-spacer" />
          <Segmented size="small" className="dw-seg-outline" value={groupBy} onChange={setGroupBy}
            options={[{ label: 'By date', value: 'date' }, { label: 'By customer', value: 'customer' }]} />
          <span className="dw-div" />
          <Tooltip title="Show only work orders past their delivery date that aren't fully ready to dispatch">
            <Space size={6}>
              <Switch size="small" className="dw-switch-red" checked={lateOnly} onChange={setLateOnly} />
              <Text style={{ fontSize: 12, color: lateOnly ? '#dc2626' : undefined }} type={lateOnly ? undefined : 'secondary'}>Late only</Text>
            </Space>
          </Tooltip>
          <Space size={6}>
            <Switch size="small" className="dw-switch-green" checked={includeCompleted} onChange={setIncludeCompleted} />
            <Text type="secondary" style={{ fontSize: 12 }}>Completed</Text>
          </Space>
          <Tooltip title="Refresh"><Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading} /></Tooltip>
        </div>
      </Card>

      {error ? <Alert type="error" showIcon className="mb-2" message={error} /> : null}

      {/* Dense metric strip */}
      <Card size="small" className="mb-2" bodyStyle={{ padding: '10px 8px' }} loading={loading && !data}>
        <div className="dw-metrics">
          {[
            { l: 'Orders', v: formatInt(kpis ? kpis.workOrders : 0), s: kpis && kpis.overdueWos ? `${kpis.overdueWos} overdue` : ' ' },
            { l: 'Committed', v: formatInt(kpis ? kpis.committed : 0), s: 'pcs' },
            { l: 'Ready', v: formatInt(kpis ? kpis.ready : 0), color: '#16a34a', s: kpis && kpis.committed ? `${Math.round((kpis.ready / kpis.committed) * 100)}% of order` : ' ' },
            { l: 'Pending', v: formatInt(kpis ? kpis.pending : 0), color: kpis && kpis.pending > 0 ? '#b45309' : undefined, s: 'pcs' },
            { l: 'Area', v: formatArea(kpis ? kpis.sqm : 0), u: 'm²', s: kpis ? `${formatArea(kpis.sqft)} ft²` : ' ' },
            { l: 'Weight', v: formatWeight(kpis ? kpis.weightKg : 0), u: 'kg', color: '#1677ff', s: kpis && kpis.weightUnknownWos > 0 ? `excl. ${kpis.weightUnknownWos} WO` : ' ' }
          ].map((m) => (
            <div className="dw-metric" key={m.l}>
              <div className="m-lbl">{m.l}</div>
              <div className="m-val" style={m.color ? { color: m.color } : undefined}>{m.v}{m.u ? <span className="m-u">{m.u}</span> : null}</div>
              <div className="m-sub">{m.s}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Delivery timeline (the cockpit's signature) */}
      {data && data.timeline && data.timeline.length ? (
        <Card size="small" className="mb-2" bodyStyle={{ padding: '8px 12px' }}
          title={<span style={{ fontSize: 13 }}>Delivery timeline</span>}
          extra={<Text type="secondary" style={{ fontSize: 11 }}>tap a day to focus it</Text>}>
          <Timeline timeline={data.timeline} from={fromIso} to={toIso} onPickDay={onPickDay} />
        </Card>
      ) : null}

      {(jumboN > 0 || capped) ? (
        <div className="mb-2 flex flex-col gap-2">
          {jumboN > 0 ? (
            <Alert type="warning" showIcon banner icon={<WarningOutlined />}
              message={`${jumboN} jumbo panel${jumboN > 1 ? 's' : ''} (≥ 2400 mm on a side or ≥ 4 m²) — need A-frame handling.`} />
          ) : null}
          {capped ? (
            <Alert type="info" showIcon banner
              message={`Showing the first ${rows.length} of ${total} — the metrics and timeline are exact for the whole window; narrow it to list them all.`} />
          ) : null}
        </div>
      ) : null}

      {/* Grouped tables */}
      <Card size="small" bodyStyle={{ paddingTop: 6 }}
        title={<span style={{ fontSize: 13 }}>{lateOnly
          ? <span style={{ color: '#dc2626' }}>Overdue orders</span>
          : 'Committed orders'}</span>}
        extra={<Text type="secondary" style={{ fontSize: 12 }}>{lateOnly
          ? `${total} overdue · as of ${fmtDay(toIso)}`
          : `${total} order${total === 1 ? '' : 's'} · ${fmtDay(fromIso)} → ${fmtDay(toIso)}`}</Text>}>
        {loading && !data ? <div className="py-6 text-center"><Spin /></div> : null}
        {!loading && data && !rows.length ? (
          <div className="py-6 text-center"><Text type="secondary">{lateOnly
            ? 'No overdue orders — everything due so far is ready to dispatch.'
            : 'Nothing due in this window. Widen the range or pick “All upcoming”.'}</Text></div>
        ) : null}
        {groups.map((g) => (
          <div key={g.key} className="mb-2">
            <div className="dw-grp-head">
              <span>{groupBy === 'customer' ? g.key : `${fmtDay(g.key)} · ${dow(g.key)}`}</span>
              <span className="dw-grp-meta">{`${g.rows.length} WO · ${g.ready}/${g.committed} ready · ${formatArea(g.sqm)} m²`}</span>
            </div>
            <Table rowKey="workOrderId" size="small" columns={columns} dataSource={g.rows} pagination={false}
              scroll={{ x: 'max-content' }} />
          </div>
        ))}
      </Card>

      <LineItemsModal open={modal.open} row={modal.row} onClose={() => setModal({ open: false, row: null })} />
    </div>
  );
}
